import React, { useEffect, useMemo, useState } from 'react';
import { Link, LinkProps, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  TbCalendar,
  TbChevronRight,
  TbCircleCheck,
  TbMail,
  TbMailOpened,
  TbNotification,
  TbSettingsCheck,
  TbUserCheck,
  TbUsers,
} from 'react-icons/tb';
import { twMerge } from 'tailwind-merge';
import PageHeader from '../components/misc/PageHeader';
import { Event, EventViewType } from '../../types/event';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import Button from '../components/buttons/Button';
import connectionService from '../../services/community/connectionService';
import { RootState } from '../../redux/reducers';
import eventService from '../../services/community/eventService';
import useEvents from '../../hooks/community/useEvents';
import OverviewCard from '../components/cards/OverviewCard';
import KpiCard from '../components/cards/KpiCard';
import SectionHeader from '../components/misc/SectionHeader';
import { isConnectionVerafied } from '../../utils/connectionUtils';
import communicationsService from '../../services/community/communicationsService';
import useCommunications from '../../hooks/community/useCommunications';
import {
  CommunicationType,
  ScheduledEmail,
  ScheduledNotification,
} from '../../types/communication';
import Avatar from '../components/misc/Avatar';
import { formatDateAndTime } from '../../utils/stringUtils';
import { taskService } from '../../services/community/taskService';
import useTasks from '../../hooks/community/useTasks';
import FeedbackCards from '../components/cards/FeedbackCards';
import EmptyScreenView from '../components/misc/EmptyScreenView';
import TrialBanner from '../components/misc/TrialBanner';
import communityService from '../../services/community/communityService';
import CommunicationBadge from '../components/badges/CommunicationBadge';

export default function OverviewPage() {
  const community = useSelectedCommunity();
  const { t } = useTranslation();
  const onboardingStep = useSelector((state: RootState) => state.application.onboardingStep);
  const firstName = useSelector((state: RootState) => state.application.firstName);
  const connections = useSelector((state: RootState) => state.connections);
  const role = useSelector((state: RootState) => state.application.role);
  const navigate = useNavigate();
  const allEvents = useEvents(EventViewType.ALL);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    communityService.fetchCommunity();
  }, []);

  useEffect(() => {
    const hasInviteFields = connections.length >= 1;
    const hasFiveMembers = connections.length >= 5;
    const hasAnEvent = allEvents.length > 0;

    setHasCompletedOnboarding(hasInviteFields && hasFiveMembers && hasAnEvent);
  }, [allEvents, community?.id, connections]);

  useEffect(() => {
    if (onboardingStep !== undefined) navigate('/contacts');
  }, []);

  useEffect(() => {
    if (community?.id !== undefined) {
      connectionService.getConnections(true);
      eventService.getUpcomingEvents().then(() => eventService.prependPastEvents());
      communicationsService.getNotifications();
      communicationsService.getEmails();
      taskService.getAllTasksForCommunity(community.id);
    }
  }, [community?.id]);

  if (!community) return <></>;

  function shouldShowTrialBanner(): boolean {
    if (!community?.trialEndDate || !community?.trialType || community?.subscription?.type)
      return false;
    if (community.paidByBusiness) return false;
    const now = new Date();
    const trialEnd = new Date(community.trialEndDate);
    const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return trialEnd.getTime() > now.getTime() && daysLeft < 31;
  }

  return (
    <>
      <PageHeader
        title={t('page.overview.greeting', { name: firstName })}
        subtitle={t('page.overview.subtitle')}
        className="pb-4"
      />

      {community.trialEndDate && community.trialType && shouldShowTrialBanner() && (
        <TrialBanner trialEndDate={new Date(community.trialEndDate)} plan={community.trialType} />
      )}

      {!hasCompletedOnboarding && (
        <div className="mb-4">
          <SetupCard />
        </div>
      )}

      <OverviewKpiRow />

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <EventsSection />
        <div className="flex flex-col gap-4">
          {community?.features?.TASKS && <TasksSection />}
          <RecentCommunicationSection />
        </div>
      </div>

      {role === 'ADMIN' && (
        <div className="mt-10">
          <div className="relative h-80 w-full rounded-[12px] bg-gradient-to-br from-primary-300 to-secondary p-4 before:absolute before:inset-0 before:animate-blur-breathe before:bg-gradient-to-br before:from-primary-300 before:to-secondary before:content-['']">
            <div className="absolute left-1 top-1 flex h-[19.5rem] w-[calc(100%-0.5rem)] flex-col rounded-[12px] bg-primary-900 p-4 text-secondary-50">
              <h2 className="text-2xl text-secondary-50">🚀 A race to the top!</h2>
              <p className="w-full text-secondary-50 md:w-[40rem]">
                Show that your association is the best by inviting as many participating communities
                as possible! We offer you a free month of PRO subscription for every participating
                community that is a part of your association. Terms and conditions apply.
              </p>
              <div className="mt-4 flex flex-1 flex-wrap items-end gap-2 self-end">
                <Button onClick={() => navigate('/race')} className="text-secondary-50">
                  Learn more
                </Button>
                <Button className="bg-gradient-to-br from-primary-300 to-secondary-200 text-primary-900 shadow-sm shadow-primary-300 hover:shadow-md hover:shadow-primary-300">
                  Join now!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <FeedbackCards className="mt-8 gap-4 px-0" />
    </>
  );
}

function OverviewKpiRow(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.overview' });
  const connections = useSelector((state: RootState) => state.connections);
  const events = useEvents(EventViewType.PAST);
  const communications = useCommunications();

  const verafiedPct =
    connections.length > 0
      ? Math.round(
          (connections.filter(isConnectionVerafied).length / connections.length) * 100,
        )
      : 0;

  const attendancePct = useMemo(() => {
    const participants = events.flatMap((e) => e.participants);
    if (participants.length === 0) return 0;
    return Math.round(
      (participants.filter((p) => p.answer === 'YES').length / participants.length) * 100,
    );
  }, [events]);

  const messagesSent =
    (communications?.notifications?.length ?? 0) + (communications?.emails?.length ?? 0);

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiCard label={t('contacts')} value={connections.length} icon={TbUsers} />
      <KpiCard label="Verafied" value={verafiedPct} suffix="%" icon={TbUserCheck} />
      <KpiCard label={t('attendance')} value={attendancePct} suffix="%" icon={TbCalendar} />
      <KpiCard label={t('messagesSent', { defaultValue: 'Messages sent' })} value={messagesSent} icon={TbMailOpened} />
    </div>
  );
}

function EventsSection(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.overview' });
  const events = useEvents(EventViewType.FUTURE);
  const upcomingEvents = events.filter((event) => event.scheduleId === null);

  return (
    <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
      <SectionHeader
        title={t('upcomingEvents')}
        count={upcomingEvents.length}
        seeAllText={t('seeAllEvents')}
        seeAllLinkProps={{ to: '/events' }}
      />
      <div className="flex flex-col gap-2 p-3">
        {upcomingEvents.length === 0 && <EmptyScreenView title="" subtitle={t('noEvents')} />}
        {upcomingEvents.slice(0, 3).map((event) => (
          <EventItem event={event} key={event.id} />
        ))}
      </div>
    </div>
  );
}

function EventItem({ event }: { event: Event }) {
  const { title, picture, startTime, participants, location } = event;
  const attending = participants.filter((p) => p.answer === 'YES').length;

  return (
    <Link to="/events/details" state={{ eventId: event.id }} className="group">
      <div className="flex items-center justify-between gap-2 rounded-[8px] bg-secondary-200/70 p-3 transition-colors hover:bg-secondary-200">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[8px] bg-primary-100 text-primary-900">
            {picture ? (
              <Avatar
                src={picture}
                alias={title.toUpperCase().split(' ').slice(0, 2).join('+')}
                className="h-10 w-10 text-sm"
              />
            ) : (
              <TbCalendar size={18} />
            )}
          </div>
          <div className="min-w-0">
            <h4 className="truncate font-semibold text-primary-900">{title}</h4>
            <p className="truncate text-sm text-gray-500">
              {[formatDateAndTime(startTime), location].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <span className="rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-900">
            {attending} attending
          </span>
          <TbChevronRight className="text-lg text-primary-300 transition-opacity group-hover:opacity-60" />
        </div>
      </div>
    </Link>
  );
}

function TasksSection(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.overview' });
  const tasks = useTasks();

  return (
    <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
      <SectionHeader
        title={t('toDoList')}
        count={tasks.length}
        seeAllText={t('seeAllTasks')}
        seeAllLinkProps={{ to: '/lists/task' }}
      />
      <div className="flex flex-col p-3">
        {tasks.length === 0 && <EmptyScreenView title="" subtitle={t('noTasks')} />}
        {tasks.slice(0, 5).map((task, index) => (
          <div
            key={task.taskId ?? index}
            className="flex items-center justify-between gap-3 border-b border-secondary-200 py-2.5 last:border-none">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: task.status?.color ?? '#6B7280' }}
              />
              <h4 className="truncate font-medium text-primary-900">{task.name}</h4>
            </div>
            {task.dueDate && (
              <p className="flex-shrink-0 text-sm text-gray-500">
                {formatDateAndTime(new Date(task.dueDate))}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentCommunicationSection(): JSX.Element {
  const { t } = useTranslation();
  const communications = useCommunications();
  const emails = communications?.emails ?? ([] as ScheduledEmail[]);
  const notifications = communications?.notifications ?? ([] as ScheduledNotification[]);

  const recent = useMemo(() => {
    const items = [
      ...emails.map((e) => ({
        id: `email-${e.emailId}`,
        type: CommunicationType.EMAIL as CommunicationType,
        title: e.title,
        date: new Date(e.sendDate),
        isSent: e.isSent,
      })),
      ...notifications.map((n) => ({
        id: `notif-${n.notificationId}`,
        type: CommunicationType.NOTIFICATION as CommunicationType,
        title: n.message?.slice(0, 60) || 'Notification',
        date: new Date(n.sendDate),
        isSent: n.isSent,
      })),
    ]
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 4);
    return items;
  }, [emails, notifications]);

  const sentPct = (list: { isSent: boolean }[]) =>
    list.length === 0
      ? 0
      : Math.round((list.filter((x) => x.isSent).length / list.length) * 100);

  return (
    <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
      <SectionHeader
        title={t('general.communication')}
        seeAllText={t('page.overview.seeMore')}
        seeAllLinkProps={{ to: '/communication' }}
      />
      <div className="flex flex-col gap-2 p-3">
        {recent.length === 0 && (
          <EmptyScreenView title="" subtitle={t('page.overview.noEvents')} />
        )}
        {recent.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between gap-3 rounded-[8px] bg-secondary-200/70 p-3">
            <div className="flex min-w-0 items-center gap-3">
              <CommunicationBadge type={item.type} size="sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-primary-900">{item.title}</p>
                <p className="text-xs text-gray-500">
                  {item.type === CommunicationType.EMAIL
                    ? t('page.communicationOverview.email')
                    : t('page.communicationOverview.notification')}{' '}
                  · {formatDateAndTime(item.date)}
                </p>
              </div>
            </div>
            <span className="flex-shrink-0 text-xs font-medium text-gray-500">
              {item.isSent ? '100%' : '0%'} sent
            </span>
          </div>
        ))}
        {(emails.length > 0 || notifications.length > 0) && (
          <div className="flex gap-3 px-1 pt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <TbMail className="h-3.5 w-3.5" />
              Emails {sentPct(emails)}%
            </span>
            <span className="flex items-center gap-1">
              <TbNotification className="h-3.5 w-3.5" />
              Push {sentPct(notifications)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SetupCard() {
  const { t } = useTranslation('', { keyPrefix: 'page.overview' });
  const community = useSelectedCommunity();
  const connections = useSelector((state: RootState) => state.connections);
  const allEvents = useEvents(EventViewType.ALL);

  const [hasInviteFields, setHasInviteFields] = useState(false);
  const [hasFiveMembers, setHasFiveMembers] = useState(false);
  const [hasAnEvent, setHasAnEvent] = useState(false);

  useEffect(() => {
    setHasInviteFields(
      connections.length >= 1 || !(community?.features?.MEMBER_ADMINISTRATION || false),
    );
    setHasFiveMembers(connections.length >= 5);
    setHasAnEvent(allEvents.length > 0);
  }, [allEvents, community?.id, connections]);

  return (
    <OverviewCard
      icon={TbSettingsCheck}
      title={t('finishSetup')}
      className="max-h-fit flex-1"
      totalCount={[hasAnEvent, hasFiveMembers, hasInviteFields].filter((x) => !x).length}>
      <div className="flex flex-col gap-2">
        {!hasInviteFields && (
          <OnboardingItem
            title={t('inviteFieldsMissingTitle')}
            subtitle={t('inviteFieldsMissingSubtitle')}
            linkText={t('start')}
            linkProps={{ to: '/invite-link', state: { view: 'SETUP' } }}
          />
        )}
        {!hasFiveMembers && (
          <OnboardingItem
            title={t('notEnoughMembersTitle')}
            subtitle={t('notEnoughMembersSubtitle')}
            linkText={t('continue')}
            linkProps={{ to: '/contacts' }}
          />
        )}
        {!hasAnEvent && (
          <OnboardingItem
            title={t('eventMissingTitle')}
            subtitle={t('eventMissingSubtitle')}
            linkText={t('start')}
            linkProps={{ to: '/events/create' }}
          />
        )}
      </div>
    </OverviewCard>
  );
}

function OnboardingItem({
  title,
  subtitle,
  linkText,
  linkProps: { className: linkClassName, ...linkProps },
}: {
  title: string;
  subtitle: string;
  linkText: string;
  linkProps: Omit<LinkProps, 'children'>;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[8px] bg-surface p-4 ring-1 ring-secondary-200">
      <div className="flex-1">
        <h4 className="font-semibold">{title}</h4>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
      <Link
        className={twMerge(
          'rounded-[8px] bg-primary-900 px-3 py-2 text-sm text-secondary-50 hover:opacity-80',
          linkClassName,
        )}
        {...linkProps}>
        {linkText}
      </Link>
    </div>
  );
}
